import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "./api";
import { track } from "./analytics";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If OAuth callback in progress, defer /me check
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    track("user_logged_in", { method: "password" });
    return data;
  };

  const register = async (email, password, full_name) => {
    const { data } = await api.post("/auth/register", { email, password, full_name });
    setUser(data);
    track("user_signed_up", { method: "password" });
    return data;
  };

  const logout = async () => {
    await api.post("/auth/logout");
    track("user_logged_out");
    // Clear per-session UI state (e.g., Step 2 bucket open/closed state)
    // so a fresh session starts with everything collapsed by default.
    try {
      sessionStorage.removeItem("wyb:step2-buckets-open");
    } catch { /* noop */ }
    setUser(false);
  };

  const refresh = checkAuth;

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refresh, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
